import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { System } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { ArrowLeft, Server } from 'lucide-react';

export default function NewSystem() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [system, setSystem] = useState({
    name: '',
    description: '',
    type: 'application', // valor padrão
    version: '1.0.0',
    status: 'active',
    license_type: 'commercial',
    support_level: 'basic',
    documentation_url: '',
    repository_url: ''
  });

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await User.getCurrentProfile();
      setCurrentUser(userData);
      
      // Se o usuário tem company_id, usar no sistema
      if (userData?.company_id) {
        setSystem(prev => ({ ...prev, company_id: userData.company_id }));
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Preparar dados para envio
      const systemData = {
        ...system,
        company_id: currentUser?.company_id || null,
        created_by: currentUser?.id || null
      };

      await System.create(systemData);
      navigate(createPageUrl('Systems'));
    } catch (error) {
      console.error('Error creating system:', error);
    }
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Systems'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Novo Sistema</h1>
          <p className="text-muted-foreground mt-1">
            Cadastre um novo sistema no catálogo
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Sistema *</Label>
                <Input
                  id="name"
                  value={system.name}
                  onChange={(e) => setSystem({ ...system, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Versão *</Label>
                <Input
                  id="version"
                  value={system.version}
                  onChange={(e) => setSystem({ ...system, version: e.target.value })}
                  placeholder="1.0.0"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={system.description}
                onChange={(e) => setSystem({ ...system, description: e.target.value })}
                className="h-24"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="type">Tipo do Sistema</Label>
                <Select
                  value={system.type}
                  onValueChange={(value) => setSystem({ ...system, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="application">Aplicação</SelectItem>
                    <SelectItem value="database">Banco de Dados</SelectItem>
                    <SelectItem value="server">Servidor</SelectItem>
                    <SelectItem value="service">Serviço</SelectItem>
                    <SelectItem value="framework">Framework</SelectItem>
                    <SelectItem value="library">Biblioteca</SelectItem>
                    <SelectItem value="tool">Ferramenta</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={system.status}
                  onValueChange={(value) => setSystem({ ...system, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="maintenance">Manutenção</SelectItem>
                    <SelectItem value="deprecated">Depreciado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="license_type">Tipo de Licença</Label>
                <Select
                  value={system.license_type}
                  onValueChange={(value) => setSystem({ ...system, license_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a licença" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commercial">Comercial</SelectItem>
                    <SelectItem value="open_source">Open Source</SelectItem>
                    <SelectItem value="freeware">Freeware</SelectItem>
                    <SelectItem value="proprietary">Proprietário</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support_level">Nível de Suporte</Label>
                <Select
                  value={system.support_level}
                  onValueChange={(value) => setSystem({ ...system, support_level: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o suporte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Básico</SelectItem>
                    <SelectItem value="standard">Padrão</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Empresarial</SelectItem>
                    <SelectItem value="none">Sem suporte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="documentation_url">URL da Documentação</Label>
                <Input
                  id="documentation_url"
                  value={system.documentation_url}
                  onChange={(e) => setSystem({ ...system, documentation_url: e.target.value })}
                  placeholder="https://docs.exemplo.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="repository_url">URL do Repositório</Label>
                <Input
                  id="repository_url"
                  value={system.repository_url}
                  onChange={(e) => setSystem({ ...system, repository_url: e.target.value })}
                  placeholder="https://github.com/usuario/projeto"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Systems'))}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : 'Salvar Sistema'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}